import { migrateUserPlanFragmentInventory, workbenchFromWire } from '@minglun/domain';
import type { AppBootstrap, OptimizationStudy, SolverHistoryEntry, SolverRequest, SolverResult, UserPlan } from '@minglun/domain';
import { parseMigratableSolverRecords } from '@minglun/data/solver-record-file';
import { catalogFingerprint, createTransferReferences, migrateResults, migrateStudies, parseResults, parseStudies, serializeResults, serializeStudies } from '@desktop-renderer/data-transfer';
import type { SolverProgressV2 } from '@minglun/protocol';
import type { RuntimeLogEntry, WebDesktopApi } from './web-contracts';
import { demoMode, loadPublishedCatalog, loadWorkspace, saveWorkspace, type AccountProfile } from './cloud';
import { createBrowserUserPlan } from './fresh-plan';
import { initializeStudies, selectStudy } from '@desktop-renderer/studies';
import type { Session } from '@supabase/supabase-js';
import { browserHighsWorkerCount } from './solver/highs-wasm-runner';
import { selectPublishedCatalog } from './catalog-selection';

type ProgressListener = (progress: SolverProgressV2) => void;

export async function createWebRuntime(profile: AccountProfile, session: Session): Promise<WebDesktopApi> {
  const browserSolverWorkers = browserHighsWorkerCount();
  const seedResponse = await fetch('/data/workbench.json', { cache: 'no-cache' });
  if (!seedResponse.ok) throw new Error('无法载入命轮资料库。');
  const seed = workbenchFromWire(await seedResponse.json());
  const stored = await loadWorkspace(profile, session);
  const published = await loadPublishedCatalog(session).catch(() => undefined);
  const catalog = selectPublishedCatalog(seed, published);
  window.minglunCloudLimits = {
    localPreview: demoMode,
    studyLimit: profile.studyLimit,
    resultLimit: profile.resultLimit,
    maxSolverThreads: browserSolverWorkers,
  };
  const browserPlan = withBrowserSolverLimits(stored.userPlan ? migrateStoredPlan(stored.userPlan, catalog, stored.workbench) : createBrowserUserPlan(catalog));
  const workspaceNeedsMigration = Boolean(stored.userPlan) && JSON.stringify(stored.userPlan) !== JSON.stringify(browserPlan);
  let bootstrap: AppBootstrap = {
    workbench: catalog,
    userPlan: browserPlan,
    developerMode: false,
    databasePath: '托管云端资料库',
    license: accountLicense(profile),
  };
  const progressListeners = new Set<ProgressListener>();
  const closeListeners = new Set<() => void>();
  let solveWorker: Worker | undefined;
  const runtimeLogs: RuntimeLogEntry[] = [];
  const log = (level: RuntimeLogEntry['level'], message: string): void => {
    runtimeLogs.unshift({ timestamp: new Date().toISOString(), level, message });
    runtimeLogs.splice(500);
  };
  log('信息', 'Web 工作台已载入；求解将在当前浏览器中执行。');
  log('信息', `浏览器 HiGHS 已启用 ${browserSolverWorkers} 个独立求解核心；每个 WASM 引擎保持单线程以确保稳定。`);
  if (workspaceNeedsMigration) {
    try {
      await saveWorkspace(profile, session, catalog, browserPlan);
      log('信息', '历史碎片数据已迁移为背包碎片，道具碎片初始化为 0。');
    } catch (error) {
      log('警告', `历史碎片数据已在当前会话完成迁移，但暂未同步：${error instanceof Error ? error.message : String(error)}`);
    }
  }

  const api: WebDesktopApi = {
    minimizeWindow: () => undefined,
    toggleMaximizeWindow: () => document.documentElement.requestFullscreen?.().catch(() => undefined),
    closeWindow: () => { if (history.length > 1) history.back(); },
    setWindowDirty: () => undefined,
    onCloseRequested: (listener) => {
      const onBeforeUnload = (): void => listener();
      addEventListener('beforeunload', onBeforeUnload);
      return () => removeEventListener('beforeunload', onBeforeUnload);
    },
    bootstrap: async () => structuredClone(bootstrap),
    saveState: async (state) => {
      const saved = {
        ...structuredClone(state),
        userPlan: withBrowserSolverLimits(state.userPlan),
        developerMode: false,
      };
      await saveWorkspace(profile, session, saved.workbench, saved.userPlan);
      bootstrap = { ...bootstrap, workbench: saved.workbench, userPlan: saved.userPlan, developerMode: false };
      log('信息', '方案、输入和求解记录已同步。');
      return saved;
    },
    getDatabasePath: async () => '托管云端资料库',
    getLicenseStatus: async () => accountLicense(profile),
    getRuntimeLogs: async () => structuredClone(runtimeLogs),
    clearRuntimeLogs: async () => { runtimeLogs.length = 0; log('信息', '运行日志已清空。'); return structuredClone(runtimeLogs); },
    openRuntimeLog: async () => downloadText('命轮工坊运行日志.dat', runtimeLogs.map((entry) => `[${new Date(entry.timestamp).toLocaleString('zh-CN', { hour12: false })}] ${entry.level}：${entry.message}`).join('\n')),
    solve: (request) => solveInWorker(request, progressListeners, (worker) => { solveWorker = worker; }, log),
    cancelSolve: async () => {
      // Let the worker unwind through AbortController so the pending solve
      // promise receives a normal cancelled result/error instead of hanging.
      solveWorker?.postMessage({ type: 'cancel' });
      log('警告', '正在取消当前计算。');
    },
    onSolverProgress: (listener) => { progressListeners.add(listener); return () => progressListeners.delete(listener); },
    importStudies: async () => {
      const raw = await selectTextFile('.json,.dat');
      if (!raw) return undefined;
      return parseStudies(raw, bootstrap.workbench);
    },
    exportStudies: async (studies: OptimizationStudy[], workbench) => { downloadText(studies.length === 1 ? `${safeName(studies[0]?.name ?? '方案')}.dat` : '命轮工坊方案.dat', serializeStudies(studies, workbench)); return '浏览器下载'; },
    importResultHistory: async (_workbench, studies) => {
      const raw = await selectTextFile('.json,.dat');
      if (!raw) return undefined;
      if (raw.trimStart().startsWith('{') || raw.trimStart().startsWith('[')) return parseResults(raw, bootstrap.workbench, studies);
      const parsed = parseMigratableSolverRecords(raw, bootstrap.workbench);
      // Legacy text records have no trustworthy catalog fingerprint. Rebuild
      // their derived data against the catalog currently published by admin.
      const preview = migrateResults(parsed.records.map((entry) => ({ ...entry, workbenchVersion: '' })), bootstrap.workbench, undefined, studies);
      preview.warnings.unshift(...(parsed.migrationWarnings ?? []));
      return preview;
    },
    exportResultHistory: async (entries, workbench) => { downloadText('命轮工坊求解记录.dat', serializeResults(entries, workbench)); return '浏览器下载'; },
    exportResult: async (entry, workbench) => { downloadText(`${safeName(entry.title)}.dat`, serializeResults([entry], workbench)); return '浏览器下载'; },
  };
  return api;
}

function accountLicense(profile: AccountProfile): AppBootstrap['license'] {
  const expired = profile.expiresAt ? new Date(profile.expiresAt).getTime() < Date.now() : false;
  const isValid = profile.status === 'active' && !expired;
  return {
    isValid,
    message: isValid ? '账户有效' : expired ? '账户已过期，请联系管理员。' : '账户已停用，请联系管理员。',
    customer: profile.displayName,
    ...(profile.expiresAt ? { expiresAt: profile.expiresAt } : {}),
    features: profile.features.includes('desktop') ? profile.features : ['desktop', ...profile.features],
  };
}

function solveInWorker(request: SolverRequest, listeners: Set<ProgressListener>, assign: (worker?: Worker) => void, log: (level: RuntimeLogEntry['level'], message: string) => void): Promise<SolverResult> {
  const worker = new Worker(new URL('./solver/solver.worker.ts', import.meta.url), { type: 'module' });
  assign(worker);
  const id = crypto.randomUUID();
  log('信息', `开始计算：${request.userPlan.name}。`);
  return new Promise((resolve, reject) => {
    let lastTick = performance.now();
    const health = (): void => {
      const now = performance.now();
      worker.postMessage({ type: 'health', visible: document.visibilityState === 'visible', delayMilliseconds: Math.max(0, now - lastTick - 1000) });
      lastTick = now;
    };
    const timer = setInterval(health, 1000);
    const visibility = (): void => { lastTick = performance.now(); health(); };
    document.addEventListener('visibilitychange', visibility);
    const cleanup = (): void => { clearInterval(timer); document.removeEventListener('visibilitychange', visibility); worker.terminate(); assign(undefined); };
    worker.onmessage = (event: MessageEvent<{ type: 'progress'; value: SolverProgressV2 } | { type: 'result'; id: string; result: SolverResult } | { type: 'error'; id: string; message: string }>) => {
      const message = event.data;
      if (message.type === 'progress') { if (message.value.runId === id) listeners.forEach((listener) => listener(message.value)); return; }
      if (message.id !== id) return;
      cleanup();
      if (message.type === 'error') { log('错误', message.message); reject(new Error(message.message)); }
      else { log('信息', `计算完成：${message.result.status}，耗时 ${(message.result.elapsedMilliseconds / 1000).toFixed(2)} 秒。`); resolve(message.result); }
    };
    worker.onerror = (event) => { cleanup(); log('错误', event.message); reject(new Error(event.message)); };
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    worker.postMessage({ type: 'solve', id, request, hardwareConcurrency: navigator.hardwareConcurrency, deviceMemory });
  });
}

function downloadText(name: string, value: string): void {
  const url = URL.createObjectURL(new Blob([value], { type: 'application/json;charset=utf-8' }));
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = name; anchor.click(); URL.revokeObjectURL(url);
}
function selectTextFile(accept: string): Promise<string | undefined> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input'); input.type = 'file'; input.accept = accept;
    input.onchange = () => { const file = input.files?.[0]; if (!file) { resolve(undefined); return; } file.text().then(resolve, reject); };
    input.click();
  });
}
function safeName(value: string): string { return value.replace(/[\\/:*?"<>|]/g, '-').slice(0, 80) || '求解结果'; }

function withBrowserSolverLimits(plan: UserPlan): UserPlan {
  const normalized = structuredClone(plan);
  normalized.solver.parameters = withoutFixedRuntimeParameters(normalized.solver.parameters);
  if (normalized.studies) {
    normalized.studies = normalized.studies.map((study) => ({
      ...study,
      solver: { ...study.solver, parameters: withoutFixedRuntimeParameters(study.solver.parameters) },
    }));
  }
  return normalized;
}

function withoutFixedRuntimeParameters(parameters: UserPlan['solver']['parameters']): UserPlan['solver']['parameters'] {
  return parameters.filter((parameter) => parameter.key !== 'time_limit_seconds' && parameter.key !== 'threads');
}

function migrateStoredPlan(source: UserPlan, workbench: AppBootstrap['workbench'], sourceWorkbench?: AppBootstrap['workbench']): UserPlan {
  const initialized = initializeStudies(structuredClone(source));
  const sameCatalog = Boolean(sourceWorkbench) && catalogFingerprint(sourceWorkbench!) === catalogFingerprint(workbench);
  const references = sourceWorkbench ? createTransferReferences(sourceWorkbench) : undefined;
  const studyPreview = migrateStudies(initialized.studies ?? [], workbench, references, sameCatalog);
  const studies = studyPreview.items.map((study, index) => ({ ...study, id: initialized.studies?.[index]?.id ?? study.id }));
  const resultPreview = migrateResults(
    initialized.solverHistory,
    workbench,
    references,
    studies,
    sameCatalog,
  );
  const solverHistory = resultPreview.items.map((entry, index) => ({
    ...entry,
    id: initialized.solverHistory[index]?.id ?? entry.id,
  }));
  const activeStudyId = studies.some((study) => study.id === initialized.activeStudyId) ? initialized.activeStudyId : studies[0]?.id;
  const migrated: UserPlan = {
    ...initialized,
    workbenchVersion: workbench.version,
    studies,
    solverHistory,
    ...(activeStudyId ? { activeStudyId } : {}),
  };
  const normalized = migrateUserPlanFragmentInventory(migrated, workbench);
  return activeStudyId ? selectStudy(normalized, activeStudyId) : normalized;
}
